// Global cache to store stack rank data when it's encountered in other components
let stackRankDataCache = {};

/**
 * Store stack rank data so it can be accessed by the prompt generator
 * @param {string} eventId - The ID of the event
 * @param {Array} ideas - The stack rank ideas array
 */
export const storeStackRankData = (eventId, ideas) => {
  if (!eventId || !ideas || !Array.isArray(ideas)) {
    console.warn('⚠️ Invalid data passed to storeStackRankData', { eventId, hasIdeas: !!ideas, isArray: Array.isArray(ideas) });
    return;
  }
  
  // console.log('📥 Storing stack rank data for eventId:', eventId, { 
  //   count: ideas.length, 
  //   firstItem: ideas[0],
  //   timestamp: new Date().toISOString() 
  // });
  
  stackRankDataCache[eventId] = ideas;
};

/**
 * Get stored stack rank data for an event
 * @param {string} eventId - The ID of the event
 * @returns {Array|null} The stack rank ideas array or null if not found
 */
export const getStoredStackRankData = (eventId) => {
  if (!eventId || !stackRankDataCache[eventId]) {
    return null;
  }
  return stackRankDataCache[eventId];
};

/**
 * Clear the stack rank data cache
 */
export const clearStackRankDataCache = () => {
  stackRankDataCache = {};
  // console.log('🧺 Stack rank data cache cleared');
};

/**
 * Fallback function in case of errors
 * @returns {string} A fallback prompt for stack rank analysis
 */
export const getStackRankFallbackPrompt = () => {
  return `You are an expert data analyst analyzing a Stack Rank survey question. With Stack Rank questions, respondents prioritize items by ranking them in order of preference.

Due to limited data availability, please provide general analysis principles for interpreting stack ranking data, including:

1. How to identify consensus vs. divergent opinions
2. Best practices for analyzing preference rankings
3. Methods for determining which items are truly prioritized vs. which show polarized responses
4. Techniques for converting ranking data into actionable insights
5. How to communicate stack ranking results effectively

Structure your response with these EXACT section headings using single # for each section:

# Executive Summary
# Key Themes
# Recommendations
# Limitations
# Survey Design Feedback
`;
};

/**
 * Generate a prompt for stack rank question analysis
 * @param {Object} eventDetails - The event details
 * @param {Object} stats - The event statistics
 * @param {Array} posts - The event posts (comments)
 * @returns {string} The generated prompt
 */
export const generateStackRankPrompt = (eventDetails, stats, posts = []) => {
  // console.log('⚙️ generateStackRankPrompt called with:', {
  //   hasEventDetails: !!eventDetails,
  //   eventDetailsKeys: eventDetails ? Object.keys(eventDetails) : [],
  //   hasStats: !!stats,
  //   statsKeys: stats ? Object.keys(stats) : [],
  //   hasPosts: !!posts,
  //   postsCount: Array.isArray(posts) ? posts.length : 0,
  //   timestamp: new Date().toISOString()
  // });

  if (!eventDetails) {
    console.error('❌ Missing eventDetails in generateStackRankPrompt');
    return null;
  }
  
  // Check for cached stack rank data for this event
  const currentEventId = eventDetails.id || eventDetails.eventId;
  const cachedRankData = getStoredStackRankData(currentEventId);
  
  // if (cachedRankData) {
  //   console.log('📦 Found cached stack rank data for eventId:', currentEventId, {
  //     count: cachedRankData.length,
  //     sample: cachedRankData.slice(0, 2),
  //     timestamp: new Date().toISOString()
  //   });
  // }
  
  // console.log('📊 Extracting stats from:', {
  //   hasStats: !!stats,
  //   allKeys: stats ? Object.keys(stats) : [],
  //   ideas: stats?.ideas,
  //   ideaCount: stats?.ideas?.length,
  //   eventData: stats?.event,
  //   summary: stats?.summary,
  //   metrics: stats?.metrics,
  //   timestamp: new Date().toISOString()
  // });
  
  if (!stats) {
    console.warn('⚠️ No stats provided to generateStackRankPrompt, returning fallback prompt');
    return `You are an expert data analyst analyzing a Stack Rank survey question. With Stack Rank questions, respondents prioritize items by ranking them in order of preference. Please provide general analysis principles for stack ranking data and best practices for interpreting such responses.`;
  }
  
  // Format event details and stats for the prompt
  const eventName = eventDetails?.name || eventDetails?.title || 'N/A';
  const questionType = eventDetails?.question_type || 'stack_rank';
  const description = eventDetails?.description || eventDetails?.content || 'N/A';
  
  // Deep inspection of event details to find question text
  // console.log('🔍 EVENT DETAILS DEEP INSPECTION:', {
  //   name: eventDetails?.name,
  //   title: eventDetails?.title,
  //   description: eventDetails?.description,
  //   content: eventDetails?.content,
  //   question: eventDetails?.question,
  //   questionText: eventDetails?.questionText,
  //   question_text: eventDetails?.question_text,
  //   prompt: eventDetails?.prompt,
  //   allKeys: eventDetails ? Object.keys(eventDetails) : []
  // });
  
  // Access metrics data from the correct path
  const metricsData = stats?.metrics || {};
  const summaryData = stats?.summary || {};
    
  // console.log('📊 StackRank prompt - accessing metrics:', { 
  //   hasMetrics: !!metricsData, 
  //   metricsDataKeys: Object.keys(metricsData),
  //   hasSummary: !!summaryData,
  //   summaryDataKeys: Object.keys(summaryData),
  //   timestamp: new Date().toISOString() 
  // });
    
  // Extract total responses with comprehensive fallback paths
  let totalResponses = 0;
    
  // Try all possible paths for total responses in metrics
  if (metricsData && typeof metricsData === 'object') {
    totalResponses = metricsData.contributed || metricsData.choices || metricsData.responses || 
                    metricsData.ideas || metricsData.totalResponses || metricsData.total ||
                    metricsData.rankings || metricsData.total_rankings || 0;
  }
    
  // If summary has total responses, use that as a fallback
  if (totalResponses === 0 && summaryData && typeof summaryData === 'object') {
    totalResponses = summaryData.total_choices || summaryData.total_responses || 
                    summaryData.choices || summaryData.responses || summaryData.total ||
                    summaryData.rankings || summaryData.total_rankings || 0;
  }
    
  // Check top level stats as a last resort
  if (totalResponses === 0) {
    totalResponses = stats.total_choices || stats.total_responses || stats.responses || 
                    stats.choices || stats.ideas || stats.contributed || stats.total ||
                    stats.total_rankings || 0;
  }
    
  // For stack ranking, we honor the principle of never using estimated data
  if (totalResponses === 0 && stats?.rankings && typeof stats.rankings === 'object') {
    console.log('📊 Stack rank - NOT estimating total responses to maintain data integrity');
  }
    
  // console.log('🔎 Stack rank - complete search for totalResponses:', {
  //   totalResponses,
  //   metricsData_paths: metricsData ? {
  //     contributed: metricsData.contributed,
  //     choices: metricsData.choices,
  //     responses: metricsData.responses,
  //     ideas: metricsData.ideas
  //   } : 'No metrics data',
  //   summaryData_paths: summaryData ? {
  //     total_choices: summaryData.total_choices,
  //     total_responses: summaryData.total_responses,
  //     choices: summaryData.choices,
  //     responses: summaryData.responses
  //   } : 'No summary data',
  //   stats_paths: {
  //     total_choices: stats.total_choices,
  //     total_responses: stats.total_responses,
  //     responses: stats.responses,
  //     choices: stats.choices,
  //     ideas: stats.ideas
  //   }
  // });
    
  // Similarly extract total users/participants with comprehensive fallback paths
  let totalUsers = 0;
    
  // Try all possible paths for total users in metrics
  if (metricsData && typeof metricsData === 'object') {
    totalUsers = metricsData.total_users || metricsData.total_user || metricsData.users || 
                metricsData.participants || metricsData.contributors || metricsData.joined || 0;
  }
    
  // If summary has total users, use that as a fallback
  if (totalUsers === 0 && summaryData && typeof summaryData === 'object') {
    totalUsers = summaryData.total_users || summaryData.total_user || summaryData.users || 
                summaryData.participants || summaryData.contributors || summaryData.joined || 0;
  }
    
  // Check top level stats as a last resort
  if (totalUsers === 0) {
    totalUsers = stats.total_user || stats.total_users || stats.users || 
                stats.participants || stats.joined || stats.viewers || 0;
  }
    
  // We will NOT estimate user count if it's not available
  if (totalUsers === 0 && totalResponses > 0) {
    console.log('📊 Stack rank - NOT estimating user count, using only available data');
  }
    
  // Calculate participation rate with validation
  let participationRate = 0;
    
  // Try to get it directly from metrics or summary first
  if (metricsData?.participation_rate !== undefined && metricsData?.participation_rate !== null) {
    participationRate = metricsData.participation_rate;
  } else if (summaryData?.participation_rate !== undefined && summaryData?.participation_rate !== null) {
    participationRate = summaryData.participation_rate;
  } else if (stats?.participation_rate !== undefined && stats?.participation_rate !== null) {
    participationRate = stats.participation_rate;
  }
  // Only calculate this from real data sources, never estimate
  else if (totalUsers > 0 && totalResponses > 0) {
    participationRate = Math.round((totalResponses / totalUsers) * 100);
    // console.log('📊 Calculating participation rate from real data:', participationRate + '%');
  }
    
  // Validate participation rate is realistic (cannot be > 100%)
  if (participationRate > 100) {
    console.log(`⚠️ Stack rank - participation rate over 100%: ${participationRate}%, capping at 100%`);
    participationRate = 100;
  }
    
  // Extract score values with enhanced validation
  let averageScore = null;
    
  // Try to find the average score in all possible locations
  if (metricsData?.average_score !== undefined && metricsData?.average_score !== null) {
    averageScore = metricsData.average_score;
  } else if (summaryData?.average_score !== undefined && summaryData?.average_score !== null) {
    averageScore = summaryData.average_score;
  } else if (stats?.average_score !== undefined && stats?.average_score !== null) {
    averageScore = stats.average_score;
  }
  
  // Log what we've found
  // console.log('🧮 Stack rank - average score search:', {
  //   averageScore,
  //   'stats.average_score': stats?.average_score,
  //   'summaryData.average_score': summaryData?.average_score,
  //   'metricsData.average_score': metricsData?.average_score
  // });
    
  // console.log('📊 Stack rank metrics extraction:', {
  //   totalResponses,
  //   totalUsers,
  //   participationRate,
  //   averageScore,
  //   timestamp: new Date().toISOString()
  // });
    
  // Get rankings data with multiple fallbacks using all possible paths
  const rankingData = stats?.rankings || {};
  
  // Try to get data from cache first if available - using currentEventId
  const cachedData = currentEventId ? getStoredStackRankData(currentEventId) : null;
  
  // console.log('🔍 Checking cached stack rank data:', { 
  //   eventId: currentEventId, 
  //   hasCachedData: !!cachedData, 
  //   cachedDataLength: cachedData ? cachedData.length : 0 
  // });
    
  // Extract ranking data with enhanced extraction logic
  let rankItems = [];
  
  // First priority: use the cached data if available - most reliable source
  if (cachedData && Array.isArray(cachedData) && cachedData.length > 0) {
    // console.log('✅ Using cached stack rank data:', { 
    //   count: cachedData.length,
    //   sample: cachedData.slice(0, 2),
    //   allKeys: cachedData[0] ? Object.keys(cachedData[0]) : [],
    //   timestamp: new Date().toISOString()
    // });
    
    // Deep inspection of cached data structure
    // console.log('🔍 DETAILED CACHED ITEM INSPECTION:', JSON.stringify(cachedData, null, 2));
    
    // Inspect each item's structure for better field mapping
    // cachedData.forEach((item, index) => {
    //   console.log(`📌 Stack Rank Item ${index + 1} RAW:`, item);
    //   console.log(`📌 Stack Rank Item ${index + 1} FIELDS:`, {
    //     name: item.name,
    //     content: item.content,
    //     title: item.title,
    //     text: item.text,
    //     avg_rating: item.avg_rating,
    //     averageRank: item.averageRank,
    //     // Additional fields that might contain item names
    //     fullName: item.fullName,
    //     displayName: item.displayName,
    //     label: item.label,
    //     value: item.value,
    //     idea_name: item.idea_name,
    //     // Fields about rankings
    //     rankings: item.rankings,
    //     choice_freq: item.choice_freq,
    //     choice_count: item.choice_count,
    //     std_dev: item.std_dev
    //   });
    // });
    
    // Enhanced mapping with more field options for item titles and rankings
    rankItems = cachedData.map(idea => {
      // First determine the title with higher priority for name/content fields
      const itemTitle = 
        idea.name || 
        idea.content || 
        idea.fullName || 
        idea.displayName ||
        idea.label ||
        idea.value ||
        idea.idea_name ||
        idea.text || 
        idea.title || 
        'Unnamed Item';
      
      // Get the ID for sorting items if needed
      const itemId = idea.id || '';
      
      return {
        title: itemTitle,
        averageRank: idea.averageRank || idea.avg_rank || idea.avg_rating || 0,
        stdDev: idea.std_dev || idea.standard_deviation || 0,
        engagement: idea.engagement || idea.engagement_score || idea.like_count || 0,
        comments: idea.comments || idea.comment_count || 0,
        rankings: idea.rankings || []
      };
    });
    
    // Log final mapped items for verification
    // console.log('🔍 Cached rank data mapped items:', { 
    //   count: rankItems.length,
    //   allItems: rankItems,
    //   timestamp: new Date().toISOString() 
    // });
  }
    
  // Try ideas path (most common)
  if (rankingData?.ideas && Array.isArray(rankingData.ideas)) {
    console.log('📊 Extracting rank items from stats.rankings.ideas');
    rankItems = rankingData.ideas.map(idea => ({
      title: idea.title || 'Unnamed Item',
      averageRank: idea.avg_rank || 0,
      topRankCount: idea.top_rank_count || 0,
      topRankPercentage: idea.top_rank_freq ? Math.round(idea.top_rank_freq * 100) : 0,
      bottomRankCount: idea.bottom_rank_count || 0,
      bottomRankPercentage: idea.bottom_rank_freq ? Math.round(idea.bottom_rank_freq * 100) : 0,
      engagement: idea.engagement_score || idea.like_count || 0,
      comments: idea.comment_count || 0
    }));
  } 
  // Try winning_ideas path
  else if (rankingData?.winning_ideas && Array.isArray(rankingData.winning_ideas)) {
    console.log('📊 Extracting rank items from stats.rankings.winning_ideas');
    rankItems = rankingData.winning_ideas.map(idea => ({
      title: idea.title || idea.name || 'Unnamed Item',
      averageRank: idea.avg_rank || idea.avg_rating || 0,
      topRankCount: idea.top_rank_count || idea.choice_count || 0,
      topRankPercentage: idea.top_rank_freq ? Math.round(idea.top_rank_freq * 100) : (idea.choice_pct ? parseInt(idea.choice_pct, 10) : 0),
      bottomRankCount: idea.bottom_rank_count || 0,
      bottomRankPercentage: idea.bottom_rank_freq ? Math.round(idea.bottom_rank_freq * 100) : 0,
      engagement: idea.engagement_score || idea.like_count || 0,
      comments: idea.comment_count || 0
    }));
    
    // Log sample data for verification
    // console.log('🔍 Winning ideas item examples:', { 
    //   original: rankingData.winning_ideas[0], 
    //   mapped: rankItems[0] 
    // });
  }
  // Try ranked_items path if present
  else if (rankingData?.ranked_items && Array.isArray(rankingData.ranked_items)) {
    console.log('📊 Extracting rank items from stats.rankings.ranked_items');
    rankItems = rankingData.ranked_items.map(item => ({
      title: item.title || 'Unnamed Item',
      averageRank: item.avg_rank || 0,
      topRankCount: item.top_rank_count || 0,
      topRankPercentage: item.top_rank_freq ? Math.round(item.top_rank_freq * 100) : 0,
      bottomRankCount: item.bottom_rank_count || 0,
      bottomRankPercentage: item.bottom_rank_freq ? Math.round(item.bottom_rank_freq * 100) : 0,
      engagement: item.engagement_score || item.like_count || 0,
      comments: item.comment_count || 0
    }));
  }
  // Try simple items array if present
  else if (rankingData?.items && Array.isArray(rankingData.items)) {
    // console.log('📊 Extracting rank items from stats.rankings.items');
    rankItems = rankingData.items.map(item => ({
      title: item.title || item.text || 'Unnamed Item',
      averageRank: item.avg_rank || 0,
      topRankCount: item.top_rank_count || 0,
      topRankPercentage: item.top_rank_freq ? Math.round(item.top_rank_freq * 100) : 0,
      bottomRankCount: item.bottom_rank_count || 0,
      bottomRankPercentage: item.bottom_rank_freq ? Math.round(item.bottom_rank_freq * 100) : 0,
      engagement: item.engagement_score || item.like_count || 0,
      comments: item.comment_count || 0
    }));
  }
  
  // console.log('📊 Extracted rank items:', {
  //   count: rankItems.length,
  //   sample: rankItems.slice(0, 2),
  //   timestamp: new Date().toISOString()
  // });
  
  // Process comments from multiple sources with fallbacks
  let comments = [];
  
  // First try to get them from the posts parameter (most reliable)
  if (Array.isArray(posts) && posts.length > 0) {
    // console.log('🗣️ Using posts for comments:', { count: posts.length });
    comments = posts.map(post => ({
      content: post.content || post.text || post.body || post.comment || post.message || '',
      author: post.author || post.user || post.username || 'Anonymous',
      likes: post.likes || post.like_count || 0,
      date: post.date || post.created_at || post.timestamp || ''
    }));
  }
  // Next try to get them from stats.comments
  else if (stats.comments && Array.isArray(stats.comments) && stats.comments.length > 0) {
    // console.log('🗣️ Using stats.comments:', { count: stats.comments.length });
    comments = stats.comments.map(comment => ({
      content: comment.content || comment.text || comment.body || comment.comment || comment.message || '',
      author: comment.author || comment.user || comment.username || 'Anonymous',
      likes: comment.likes || comment.like_count || 0,
      date: comment.date || comment.created_at || comment.timestamp || ''
    }));
  }
  // Finally try to get them from eventDetails.comments
  else if (eventDetails.comments && Array.isArray(eventDetails.comments) && eventDetails.comments.length > 0) {
    // console.log('🗣️ Using eventDetails.comments:', { count: eventDetails.comments.length });
    comments = eventDetails.comments.map(comment => ({
      content: comment.content || comment.text || comment.body || comment.comment || comment.message || '',
      author: comment.author || comment.user || comment.username || 'Anonymous',
      likes: comment.likes || comment.like_count || 0,
      date: comment.date || comment.created_at || comment.timestamp || ''
    }));
  }
  
  // console.log('🗣️ Processed comments:', { 
  //   count: comments.length,
  //   sample: comments.slice(0, 2),
  //   timestamp: new Date().toISOString()
  // });
  
  // Format the data for inclusion in the prompt
  const questionText = eventDetails.questionText || 
                      eventDetails.question_text || 
                      eventDetails.question || 
                      eventDetails.prompt ||
                      eventDetails.title || 
                      eventDetails.content ||
                      eventDetails.name ||
                      'Please rank the following items in order of preference';
  
  // Format prompt sections
  const formattedParticipationData = `Total Users: ${totalUsers || 'Unknown'}
Total Responses: ${totalResponses || 'Unknown'}
Participation Rate: ${participationRate ? participationRate + '%' : 'Unknown'}`;
  
  // Format ranking items with metrics
  const itemsFormatted = rankItems.length > 0
    ? rankItems.map((item, index) => {
        let metricText = `${index + 1}. ${item.title}`;
        
        // Only add metrics if they're real values, not estimations
        if (item.averageRank !== undefined) {
          metricText += `\n   - Average Rank: ${item.averageRank.toFixed(2)}`;
        }
        
        if (item.stdDev !== undefined && item.stdDev > 0) {
          metricText += `\n   - Standard Deviation: ${parseFloat(item.stdDev).toFixed(2)}`;
        }
        
        // Add engagement metrics if available
        if (item.engagement !== undefined && item.engagement > 0) {
          metricText += `\n   - Engagement: ${item.engagement}`;
        }
        
        return metricText;
      }).join('\n\n')
    : 'No ranking data available';
    
  // We no longer calculate polarizing items as per user preference to avoid estimations
  // Only working with actual available data
  // console.log('📊 Avoiding polarization estimates per data accuracy requirements');

  // As a last resort, try to extract from summary.winning_ideas directly if present
  if (rankItems.length === 0 && summaryData?.winning_ideas && Array.isArray(summaryData.winning_ideas)) {
    console.log('📊 Extracting rank items from direct summary.winning_ideas path');
    rankItems = summaryData.winning_ideas.map(idea => ({
      title: idea.title || idea.name || 'Unnamed Item',
      averageRank: idea.avg_rank || idea.avg_rating || 0,
      topRankCount: idea.top_rank_count || idea.choice_count || 0,
      topRankPercentage: idea.top_rank_freq ? Math.round(idea.top_rank_freq * 100) : (idea.choice_pct ? parseInt(idea.choice_pct, 10) : 0),
      bottomRankCount: idea.bottom_rank_count || 0,
      bottomRankPercentage: idea.bottom_rank_freq ? Math.round(idea.bottom_rank_freq * 100) : 0,
      engagement: idea.engagement_score || idea.like_count || 0,
      comments: idea.comment_count || 0
    }));
    
    // console.log('🔍 Direct summary.winning_ideas examples:', { 
    //   original: summaryData.winning_ideas[0], 
    //   mapped: rankItems[0] 
    // });
  }
  
  // if (rankItems.length === 0) {
  //   console.log('⚠️ No ranking data available for formatting');
  // }
    
  // Format comments for the prompt
  const formattedComments = comments.length > 0
    ? comments.map((comment, index) => {
        return `${index + 1}. "${comment.content}" - ${comment.author}${comment.likes ? ` (${comment.likes} likes)` : ''}`;
      }).join('\n')
    : 'No comments available';
  
  // Assemble the data section
  const formattedData = `Question: ${questionText}
Question Type: Stack Rank
${formattedParticipationData}`;
  
  // Format item types section if available
  // Better logging of rank items
  if (rankItems.length > 0) {
    console.log('📊 Final rank items to be included in prompt:', {
      count: rankItems.length,
      allItems: rankItems.map(item => ({
        title: item.title,
        averageRank: item.averageRank,
        stdDev: item.stdDev
      })),
      timestamp: new Date().toISOString()
    });
  }
  
  // Special case handling for days of the week
  if (rankItems.length > 0) {
    // Check if items appear to be days of the week
    const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const daysFound = rankItems.filter(item => 
      daysOfWeek.some(day => item.title.toLowerCase().includes(day.toLowerCase()))
    );
    
    // If many items match days of the week, we should sort them in proper order
    if (daysFound.length > 2) {
      console.log('📅 Detected days of the week in rank items, applying special ordering');
      
      // Create a map for day of week ordering
      const dayOrder = {
        'monday': 1,
        'tuesday': 2,
        'wednesday': 3,
        'thursday': 4,
        'friday': 5,
        'saturday': 6,
        'sunday': 7
      };
      
      // Sort items by day of week order
      rankItems.sort((a, b) => {
        // Find which day each title contains
        const dayA = Object.keys(dayOrder).find(day => a.title.toLowerCase().includes(day)) || '';
        const dayB = Object.keys(dayOrder).find(day => b.title.toLowerCase().includes(day)) || '';
        
        // Sort by day order if both contain days
        if (dayA && dayB) {
          return dayOrder[dayA] - dayOrder[dayB];
        }
        return 0; // Keep original order if not comparing days
      });
      
      // console.log('📅 Reordered days of week:', rankItems.map(item => item.title));
    }
  }
  
  const formattedItems = `## Ranked Items (${rankItems.length})
${itemsFormatted}`;
  
  // Format comments section
  const formattedCommentsSection = `## User Comments (${comments.length})
${formattedComments}
`;
    
  // Initialize the prompt template - ensuring we only use real available data
  // console.log('📚 Final verification of stack rank prompt data integrity');
  // console.log('📊 Only using real, available data points - no estimations');
  
  const promptTemplate = `# Stack Rank Analysis Task

You are an expert data analyst analyzing a Stack Rank survey. This type of survey asks respondents to rank items in order of preference or priority. Analyze the following data and provide a structured report in markdown format:

## Survey Question
${questionText}

## Ranked Items (${rankItems.length} items)
${itemsFormatted}

## User Comments (${comments.length} comments)
${formattedComments}

## Analysis Request
Provide a comprehensive analysis of this stack rank exercise. Consider patterns, trends, and insights about participant preferences. Structure your analysis with these sections:

# Executive Summary
- [Provide a concise 3-5 sentence overview of the key findings]
- [Highlight the most significant insights from the ranking data]
- [Summarize overall participant preferences and priorities]
- [Note any standout patterns or trends]

# Key Themes
- [Identify 3-5 major themes from the ranking data]
- [Focus on underlying preferences, values, and priorities]
- [Include both positive aspects and areas of concern]
- [Highlight patterns in how items were ranked]
- [If engagement data is available, note which items generated the most engagement]

# Recommendations
- [Provide 3-5 actionable next steps based on the ranking data]
- [Include specific suggestions for addressing low-ranked items]
- [Recommend strategies for leveraging high-ranked items]
- [Suggest how to address any polarizing items that show mixed responses]
- [Offer ideas for follow-up actions to gain deeper insights]

# Limitations
- [List any limitations in the data that affect the depth or breadth of analysis]
- [Suggest what additional data would be helpful]
- [Identify potential biases or gaps in the responses]
- [Comment on data quality issues if present]

# Survey Design Feedback
- [Provide suggestions for improving the survey question or format]
- [Comment on the ranking scale effectiveness]
- [Suggest improvements to the item descriptions if needed]
- [Suggest how to generate richer, more actionable, or more diverse responses]

Format your response with these EXACT section headings using single # for each section. Ensure each section has substantive content.`;

  console.log('✅ Stack rank prompt generated:', {
    promptLength: promptTemplate.length,
    timestamp: new Date().toISOString()
  });

  return promptTemplate;
};
